import React, { useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import QuizLoading from '@/shared/components/QuizLoading';
import { TestType } from '@/models/SharedTypes';
import { logger } from '@/utils/logger';
import { useUserData } from '@/contexts/AuthContext';
import { resolveTestType } from '@/utils/resolveTestType';

interface LoadingState {
  /** Null: the learner has not chosen an exam yet; they are sent to pick one. */
  testType: TestType | null;
  /** Backend-owned session shape; 'mock' = the full 2026 blueprint exam (#300). */
  sessionMode?: 'practice' | 'category' | 'mock';
  targetQuestionCount: number;
  preferredDifficulty: number;
  categories?: string[];
  startQuestionId?: string; // Deep-link: start on a specific question (email CTA)
}

const QuizLoadingRoute: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useUserData();

  // Get quiz configuration from navigation state
  const state = location.state as LoadingState | null;
  
  logger.debug('QuizLoadingRoute', 'Rendered with state', state);

  // Default fallback when quiz configuration isn't provided
  const effectiveState = state || {
    testType: resolveTestType(userData?.profile?.targetExam),
    targetQuestionCount: 30,
    preferredDifficulty: 5
  };

  useEffect(() => {
    if (!effectiveState?.testType) {
      logger.info('QuizLoadingRoute', 'No exam chosen yet, sending to the exam picker');
      navigate('/exam-date', { replace: true });
    } else if (!state) {
      logger.info('QuizLoadingRoute', 'No state provided, using default quiz configuration');
    }
  }, [effectiveState?.testType, navigate, state]);

  // Memoize the callback to prevent unnecessary re-renders of QuizLoading
  const handleQuizReady = useCallback((sessionId: string) => {
    logger.success('QuizLoadingRoute', 'Quiz ready, navigating to session', { sessionId });
    navigate(`/quiz/${sessionId}`, { replace: true });
  }, [navigate]);

  // Always render QuizLoading with effective state (fallback included)
  return (
    <QuizLoading
      testType={effectiveState.testType ?? undefined}
      sessionMode={effectiveState.sessionMode}
      targetQuestionCount={effectiveState.targetQuestionCount}
      preferredDifficulty={effectiveState.preferredDifficulty}
      categories={effectiveState.categories}
      startQuestionId={effectiveState.startQuestionId}
      onQuizReady={handleQuizReady}
    />
  );
};

export default QuizLoadingRoute;